import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/assets";
import { AudioWaveform, SlidersHorizontal, Disc3, Users, Rocket, ArrowUpRight, X, Check } from "lucide-react";

const SERVICES = [
  {
    name: "Music Production",
    desc: "Custom beats and full track production built from scratch — tailored to your voice, your lane, your moment.",
    icon: AudioWaveform,
    image: IMAGES.studioConsole1,
    span: "lg:col-span-2",
    overview: "Custom beats and full track production built from scratch — tailored to your voice, your lane, your moment. From heavy Trap and Drill sub-bass lines to smooth R&B/Soul melodies and high-energy Bounce, our production team crafts original arrangements built around your tone, pitch, and brand identity.",
    deliverables: [
      "1-on-1 Creative Pre-Production — session mapping, reference track analysis, BPM tuning, and key selection matched to your vocal range",
      "Custom Instrumental Creation — original compositions engineered with industry-grade virtual instruments, analog synthesizers, and custom drum design",
      "Full Multi-Track Stems — unmixed & processed WAV stems (drums, sub-bass, lead synths, pads, FX, melodies) for studio vocal recording",
      "Arrangement Revisions — up to 3 rounds of structural edits (intro, hook, verses, bridge, outro, drops) to match your lyric arrangement",
      "Commercial Rights Transfer — full work-for-hire documentation or agreed royalty split agreements, ready for registration",
    ],
    cta: "Book Production Session",
  },
  {
    name: "Mixing & Mastering",
    desc: "Radio-ready polish. Surgical mixing and competitive loudness that translates on every system.",
    icon: SlidersHorizontal,
    span: "",
    overview: "Radio-ready polish: surgical mixing and competitive loudness that translates seamlessly on every system — from high-end club rigs and car audio to phone speakers and AirPods. We clean up vocal mud, balance instrumentals, optimize low-end response, and apply digital mastering to hit streaming standards.",
    deliverables: [
      "Surgical Vocal Processing — precision EQ, multi-band compression, pitch correction (Autotune / Melodyne), de-essing, and spatial FX chains",
      "Instrumental Balancing & Low-End Control — phase alignment, sub-bass management, and sidechain compression for punch and vocal room",
      "Mastering for Streaming — peak limiting and target LUFS normalization (-14 LUFS DSP standard or competitive loud masters)",
      "Final Master Package — 24-bit/44.1kHz hi-res WAV master, 320kbps streaming MP3, performance/TV track, and clean radio edit",
      "Revision Rounds — 2 complimentary mix revision passes included",
    ],
    cta: "Book Mixing & Mastering",
  },
  {
    name: "Beat Licensing",
    desc: "Lease or own exclusive rights to lab-grade instrumentals. Instant contracts, zero ambiguity.",
    icon: Disc3,
    span: "",
    overview: "Cold-Blooded Soundscapes™ — the label's digital beat catalog. Four license tiers from a $29 MP3 lease to a $499 exclusive buyout with trackout stems. Every purchase includes a generated license agreement and instant delivery.",
    deliverables: [
      "MP3 Lease ($29) — 320kbps MP3, up to 50,000 streams",
      "WAV Lease ($59) — WAV + MP3, up to 250,000 streams",
      "Unlimited ($149) — unlimited streams, shows, and monetized videos",
      "Exclusive ($499) — trackout stems + WAV + MP3, beat retired from the catalog forever",
    ],
    cta: "Browse the Beat Store",
    ctaTo: "/beats",
  },
  {
    name: "Artist Management",
    desc: "Career architecture, branding, rollout strategy and deal guidance from people in your corner.",
    icon: Users,
    span: "",
    overview: "Career architecture from executives who built the label themselves — branding, rollout strategy, deal guidance, and mentorship from Sherrod 'HotRod' Whiteside's business desk and Heather Da Boa's creative direction.",
    deliverables: [
      "Rollout & release strategy — singles, EPs, and campaign timing built around your momentum",
      "Brand identity development — image, voice, and visual direction that reads premium everywhere",
      "Deal guidance — independent-first structures that protect your catalog and your leverage",
    ],
    cta: "Request a Quote",
  },
  {
    name: "Distribution",
    desc: "Your record on every major platform with royalty tracking, release planning and playlist pitching.",
    icon: Rocket,
    image: IMAGES.studioNeon,
    span: "lg:col-span-2",
    overview: "Your record on every major platform — with royalty tracking, release planning, and playlist pitching handled by the label. ASCAP® & PRO-registered workflows keep your publishing clean from day one.",
    deliverables: [
      "Release planning & scheduling — timeline, assets, and metadata done right the first time",
      "Playlist pitching & DSP strategy — Spotify, Apple Music, and editorial positioning",
      "Royalty & publishing tracking — ASCAP® / PRO registration guidance so every stream is accounted for",
    ],
    cta: "Request a Quote",
  },
];

export const ServicesSection = ({ onQuote }) => {
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [active]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleCta = (s) => {
    setActive(null);
    if (s.ctaTo) navigate(s.ctaTo);
    else onQuote(s.name);
  };

  return (
  <section id="services" className="relative py-20 lg:py-28 bg-charcoal/40" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
      <Reveal className="max-w-2xl">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4">
          Services
        </p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
          Everything an artist needs.
          <br />
          <span className="text-stroke-ice">Under one roof.</span>
        </h2>
        <p className="mt-4 text-sm text-ice/50">Click any service for the full breakdown of what&rsquo;s included.</p>
      </Reveal>

      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={s.name} delay={i * 0.08} className={s.span}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setActive(s)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setActive(s)}
                className="group relative h-full min-h-[260px] cursor-pointer bg-charcoal border border-crystal/20 rounded-2xl overflow-hidden hover:border-venom/50 hover:-translate-y-1.5 hover:shadow-[0_16px_50px_rgba(57,255,20,0.14)] transition-all duration-500 flex flex-col justify-end p-6 sm:p-8"
                data-testid={`service-card-${i}`}
              >
                {s.image && (
                  <>
                    <img
                      src={s.image}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/30" />
                  </>
                )}
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl bg-crystal/15 border border-crystal/40 flex items-center justify-center mb-5 group-hover:bg-venom/15 group-hover:border-venom/50 transition-colors duration-500">
                    <Icon size={20} className="text-venom" />
                  </div>
                  <h3 className="font-display text-lg sm:text-xl font-bold text-white">{s.name}</h3>
                  <p className="mt-2.5 text-sm text-ice/65 leading-relaxed max-w-md">{s.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-venom group-hover:text-white transition-colors duration-300" data-testid={`service-quote-btn-${i}`}>
                    See what&rsquo;s included
                    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>

    <AnimatePresence>
      {active && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-obsidian/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={() => setActive(null)}
          data-testid="service-modal"
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-2xl max-h-[88vh] overflow-y-auto bg-charcoal border border-crystal/30 rounded-t-3xl sm:rounded-2xl p-6 sm:p-10 shadow-[0_30px_100px_rgba(0,0,0,0.6)]"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-xl bg-crystal/15 border border-crystal/40 flex items-center justify-center shrink-0">
                  <active.icon size={22} className="text-venom" />
                </span>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-white" data-testid="service-modal-title">{active.name}</h3>
              </div>
              <button onClick={() => setActive(null)} aria-label="Close service details" className="p-2 text-ice/50 hover:text-white transition-colors" data-testid="service-modal-close">
                <X size={20} />
              </button>
            </div>
            <p className="mt-5 text-sm sm:text-base text-ice/70 leading-relaxed">{active.overview}</p>
            <p className="mt-7 font-mono text-[10px] uppercase tracking-[0.25em] text-crystal">What&rsquo;s included</p>
            <ul className="mt-3 space-y-3">
              {active.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-3 text-sm text-ice/75 leading-relaxed">
                  <Check size={15} className="text-venom shrink-0 mt-0.5" />
                  {d}
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleCta(active)}
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-venom text-obsidian font-bold text-sm px-8 py-4 rounded-full hover:glow-venom active:scale-95 transition-all duration-300"
              data-testid="service-modal-cta"
            >
              {active.cta} <ArrowUpRight size={16} />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  </section>
  );
};
